/**
 * Theme registry for the design exploration.
 *
 * Adding a sixth direction is a two-step change: drop a CSS file into
 * `tokens/themes/`, `@import` it from `design-system.css`, and add an entry
 * here. Nothing else in the system needs to know.
 */

export type ThemeId =
  | 'precision'
  | 'saga-gold'
  | 'aurora-noir'
  | 'liquid-mercury'
  | 'tactile';

export type Mode = 'light' | 'dark';
export type Density = 'compact' | 'comfortable';

export type ThemeMeta = {
  id: ThemeId;
  name: string;
  tagline: string;
  mood: string;
  /** Why this direction exists and what it is betting on. */
  rationale: string;
  pros: string[];
  cons: string[];
  bestFor: string;
  /** Accessibility note specific to this direction. */
  a11y: string;
  /** Mode this direction is designed around; the other is still supported. */
  defaultMode: Mode;
  /** Four representative colours, for the picker swatch strip. */
  swatches: string[];
};

export const THEMES: ThemeMeta[] = [
  {
    id: 'precision',
    name: 'Precision',
    tagline: 'Modern enterprise, maximum density',
    mood: 'Efficient · clean · scalable',
    rationale:
      'The recommended baseline. A 4px grid, hairline border-first elevation, one accent (Icelandair navy) and a true tonal neutral ramp. It is deliberately understated: the goal is that nobody notices the UI, they just get through the work faster.',
    pros: [
      'Highest information density of the five',
      'Cheapest to roll out across 28 pages',
      'Ages well — no trend attached to it',
    ],
    cons: ['Least "designed" at first glance', 'Little brand personality on its own'],
    bestFor: 'The default. Dense operational screens used all shift.',
    a11y:
      'Body text 16.8:1, muted 5.7:1 on the page background. Elevation is carried by borders, so surfaces stay distinguishable without relying on shadow perception.',
    defaultMode: 'light',
    swatches: ['#123A7D', '#F7F8FA', '#E3E6EB', '#0F172A'],
  },
  {
    id: 'saga-gold',
    name: 'Saga Gold',
    tagline: 'Heritage Icelandair, modernised',
    mood: 'Branded · premium · trustworthy',
    rationale:
      'Deep navy ink on warm paper, with the classic golden identity treated as a scarce resource — it appears on the confirmation action and the sidebar active marker, nowhere else. Scarcity is what keeps gold reading as premium instead of dated.',
    pros: [
      'Instantly recognisable as Icelandair',
      'The confirm action carries real brand weight',
      'Warm neutrals are easier on the eye than pure white',
    ],
    cons: [
      'Gold is a contrast trap — needs constant discipline',
      'Strongest opinion of the five; hardest to walk back',
    ],
    bestFor: 'Screens seen by people outside the maintenance org.',
    a11y:
      'Gold surfaces always pair with near-black #1A1A1A (9.6:1, and still 6.2:1 at the darkest end of the gradient). White on gold is 1.8:1 and is never used. The gold marker is an edge, not a fill, so it never becomes a text background.',
    defaultMode: 'light',
    swatches: ['#F5B53F', '#10264B', '#F6F3ED', '#1B2437'],
  },
  {
    id: 'aurora-noir',
    name: 'Aurora Noir',
    tagline: 'Dark, quiet, expensive',
    mood: 'Refined · calm · confident',
    rationale:
      'Layered near-blacks separated by hairline light borders rather than shadows, with a restrained platinum-blue accent. Beyond looks there is a practical argument: hangar and ops rooms run dim and shifts are long, and a dark surface makes the frozen bright status hues more legible, not less.',
    pros: [
      'Genuinely easier on the eyes for long/night shifts',
      'Fixed status colours punctuate instead of competing',
      'Reads as premium equipment rather than software',
    ],
    cons: [
      'Bright status hues need neutral containers or they shout',
      'Printing and screenshots need the light mode',
    ],
    bestFor: 'Line/hangar monitors, night shift, control-room displays.',
    a11y:
      'Text 17.3:1 on the base surface, muted 7.9:1. Dark themes can over-saturate, so accents are desaturated and status hues sit in muted containers with text labels alongside.',
    defaultMode: 'dark',
    swatches: ['#9CC4E4', '#08090B', '#212530', '#ECEFF3'],
  },
  {
    id: 'liquid-mercury',
    name: 'Liquid Mercury',
    tagline: 'Fluid metal, strictly rationed',
    mood: 'Modern · high-tech · striking',
    rationale:
      'A fluid-metal language — multi-stop metallic gradients, specular highlights, frosted overlays. The discipline that stops it being gimmicky is confinement: the liquid treatment touches exactly three things (primary button, sidebar active pill, header). Tables and inputs stay flat, because a shimmering table is unusable.',
    pros: [
      'The most distinctive of the five',
      'Primary actions are impossible to miss',
      'Frosted overlays give real depth hierarchy',
    ],
    cons: [
      'Easiest direction to overdo in later hands',
      'Gradient buttons need contrast checking at every stop',
      'backdrop-filter has a real rendering cost on weak hardware',
    ],
    bestFor: 'A flagship screen or a product-marketing surface.',
    a11y:
      'The metal ramp is light across all stops so the label stays near-black throughout. The specular sweep is decorative and disabled under prefers-reduced-motion.',
    defaultMode: 'light',
    swatches: ['#A9B5C6', '#EDF0F4', '#1A2331', '#10151D'],
  },
  {
    id: 'tactile',
    name: 'Tactile',
    tagline: 'Controls that feel like hardware',
    mood: 'Physical · satisfying · engineered',
    rationale:
      'A four-part depth model on every button: inner top highlight, graded fill, solid bottom lip, cast shadow. Pressing collapses the lip and sinks the face 2px; releasing springs it back. Inputs invert the model and read as recessed slots.',
    pros: [
      'Best affordance of the five — everything looks operable',
      'Press feedback is genuinely satisfying with gloves on',
      'Suits a tool used next to physical equipment',
    ],
    cons: [
      'Neumorphism risk if depth ever spreads to text containers',
      'More CSS per control to maintain',
    ],
    bestFor: 'Touch-heavy shop-floor and tablet workflows.',
    a11y:
      'Borders stay real and visible, so control edges never depend on shadow perception alone. The pressed state keys off both :active and a JS-set attribute, because :active is unreliable for keyboard activation.',
    defaultMode: 'light',
    swatches: ['#1B4B73', '#EEECE8', '#BDB6A9', '#221F1B'],
  },
];

export const THEME_IDS = THEMES.map((t) => t.id);

export const getTheme = (id: string): ThemeMeta | undefined =>
  THEMES.find((t) => t.id === id);

export const isThemeId = (id: string): id is ThemeId =>
  THEME_IDS.includes(id as ThemeId);
